import { createFileRoute } from '@tanstack/react-router';
import { EvaluationTypeLabelsEnum, SummarizationNamespaces, ChatType } from '@/app/utils/constants';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Analytics01Icon,
  ArrowLeftRightIcon,
  Copy01Icon,
  SparklesIcon,
  Tick02Icon,
} from '@hugeicons/core-free-icons';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
} from '@/app/ui/components/breadcrumb';
import {
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderSidechatButton,
  Header,
  HeaderMobileChatButton,
} from '@/app/ui/components/header';
import { useTranslations } from 'use-intl';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';
import {
  MainContainer,
  MainHeader,
  MainHeaderBackButton,
  MainScrollArea,
  Main,
} from '@/app/ui/components/main';
import { H1, H2 } from '@/app/ui/components/heading';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { Spinner } from '@/app/ui/components/spinner';
import {
  useGetChangeAssessment,
  useSendChangeAssessmentFeedback,
} from '@/app/hooks/queries/useChangeAssessments';
import { useState } from 'react';
import { Card } from '@/app/ui/components/card';
import { Button } from '@/app/ui/components/button';
import { Textarea } from '@/app/ui/components/textarea';
import FeedbackButtons from '@/app/components/shared/Feedback/FeedbackButtons';
import { FeedbackValue } from '@/app/components/shared/Feedback/utils';
import {
  Alert,
  AlertActionToolbar,
  AlertActionToolbarButton,
  AlertDescription,
  AlertDescriptionCollapsibleText,
  AlertTitle,
} from '@/app/ui/components/alert';
import useDocumentPreview from '@/app/components/shared/Citations/useDocumentPreview';
import Preview from '@/app/components/shared/Preview/Preview';
import NewAssessmentBadge from '@/app/components/features/supplements/changeAssessments/details/AiAssessment/NewAssessmentBadge';
import ReactMarkdown from 'react-markdown';
import { Tabs, TabsList, TabsTab, TabsPanel } from '@/app/ui/components/tabs';
import { Tooltip, TooltipTrigger, TooltipPopup } from '@/app/ui/components/tooltip';
import { ChangeAssessmentStatement } from '@/app/types/models';
import { PerspectivesSection } from '@/app/components/features/supplements/changeAssessments/details/PerspectivesSection';
import { DetailsSection } from '@/app/components/features/supplements/changeAssessments/details/DetailsSection';
import { useCopyToClipboard } from '@/app/ui/hooks/use-copy-to-clipboard';
import { DocumentAlerts } from '@/app/components/shared/EntityDocuments/DocumentAlerts';
import AIChatProvider from '@/app/components/features/aiChat/AIChatContext';
import useEntryChat from '@/app/components/features/aiChat/hooks/useEntryChat';
import { Sidechat } from '@/app/components/shared/Sidechat';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/contract-management/$supplementId/_layout/change-analysis/$assessmentId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.contractManagement');
  const tFeature = useTranslations('features.changeAnalysis');
  const tSideChat = useTranslations('components.sideChatDrawer');
  const { contractId, projectId, supplementId, assessmentId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));

  const stream = useEntryChat({
    id: contractId,
    namespace: ChatType.SUPPLEMENT,
    supplementId,
    assessmentId,
  });

  const { data: assessment, isLoading } = useGetChangeAssessment({
    contractSupplementPackageId: supplementId,
    changeAssessmentId: assessmentId,
  });

  return (
    <AIChatProvider
      chatType={ChatType.SUPPLEMENT}
      sourceStream={stream}
      chatInitialOpen={false}
      evaluationName={evaluation.name}
      evaluationType={EvaluationTypeLabelsEnum.CONTRACT}
      evaluationId={contractId}
      contextName={tSideChat('changeAssessment')}
    >
      <Main>
        <Header>
          <HeaderBackButton
            to="/project/$projectId/contracts/$contractId/contract-management/$supplementId/change-analysis"
            params={{ projectId, contractId, supplementId }}
          />
          <HeaderSidebarTrigger />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbMenu>
                <BreadcrumbMenuItem
                  to="/project/$projectId/contracts/$contractId"
                  params={{ projectId, contractId }}
                >
                  <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                  {evaluation.name}
                </BreadcrumbMenuItem>
                <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                  <ProjectTypeIcon type={project.type} />
                  {project.name}
                </BreadcrumbMenuItem>
              </BreadcrumbMenu>
              <BreadcrumbSeparator className="max-md:hidden" />
              <BreadcrumbItem className="max-md:hidden">
                <BreadcrumbLink
                  to="/project/$projectId/contracts/$contractId/contract-management/$supplementId"
                  params={{ projectId, contractId, supplementId }}
                >
                  <HugeiconsIcon icon={ArrowLeftRightIcon} />
                  {t('titleLong')}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="max-md:hidden" />
              <BreadcrumbItem>
                <BreadcrumbLink
                  to="/project/$projectId/contracts/$contractId/contract-management/$supplementId/change-analysis"
                  params={{ projectId, contractId, supplementId }}
                >
                  <HugeiconsIcon icon={Analytics01Icon} />
                  {tFeature('title')}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{assessment?.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <HeaderSidechatButton />
          <HeaderMobileChatButton />
        </Header>
        <MainScrollArea>
          <DocumentAlerts
            resourceId={supplementId}
            summaryType={SummarizationNamespaces.SUPPLEMENT}
          />
          {isLoading ? (
            <Spinner kind="inset" />
          ) : assessment ? (
            <>
              <MainHeader>
                <H1>
                  <MainHeaderBackButton
                    to="/project/$projectId/contracts/$contractId/contract-management/$supplementId/change-analysis"
                    params={{ projectId, contractId, supplementId }}
                  />
                  {assessment.title}
                </H1>
              </MainHeader>
              <MainContainer>
                <AiAssessmentSection />
                <DetailsSection />
                <PerspectivesSection />
                <KeyArgumentsSection />
                <FeedbackCard
                  feedback={assessment.isPositiveFeedback}
                  userReview={assessment.userReview}
                  supplementId={supplementId}
                  assessmentId={assessmentId}
                />
              </MainContainer>
            </>
          ) : null}
        </MainScrollArea>
      </Main>
      <Sidechat />
    </AIChatProvider>
  );
}

function AiAssessmentSection() {
  const t = useTranslations('components.changeAssessments.details');
  const tGeneral = useTranslations('general');
  const copy = useCopyToClipboard();
  const { assessmentId, supplementId } = Route.useParams();
  const { data } = useGetChangeAssessment({
    contractSupplementPackageId: supplementId,
    changeAssessmentId: assessmentId,
  });

  if (!data) return null;

  const aiAssessment = data.aiAssessment || '';

  return (
    <>
      <Alert className="mb-5">
        <AlertTitle className="flex items-start">
          <HugeiconsIcon
            icon={SparklesIcon}
            className="text-primary-11 inline-block size-4.5 mr-1.5 mb-1"
          />
          {t('aiAssessment')}{' '}
          <AlertActionToolbar>
            <AlertActionToolbarButton
              label={copy.copied ? tGeneral('copied') : tGeneral('copyToClipboard')}
              onClick={() => copy.trigger(aiAssessment)}
            >
              <HugeiconsIcon icon={copy.copied ? Tick02Icon : Copy01Icon} />
            </AlertActionToolbarButton>
          </AlertActionToolbar>
        </AlertTitle>
        <AlertDescription>
          <NewAssessmentBadge className="font-medium mb-3 mt-1" verdict={data.verdict} />
          <AlertDescriptionCollapsibleText text={aiAssessment} />
        </AlertDescription>
      </Alert>
    </>
  );
}

function KeyArgumentsSection() {
  const t = useTranslations('components.changeAssessments.details');
  const { assessmentId, supplementId } = Route.useParams();
  const { data: assessment } = useGetChangeAssessment({
    contractSupplementPackageId: supplementId,
    changeAssessmentId: assessmentId,
  });

  if (!assessment) return null;

  const contractStatements = assessment.mainContractStatements;
  const changeRecordStatements = assessment.changeRecordStatements;
  const otherDocsStatements = assessment.otherStatements;

  const tabs = [
    {
      label: t('signedContract', { count: contractStatements?.length ?? 0 }),
      value: 'contract',
      statements: contractStatements,
    },
    {
      label: t('changeRecords', { count: changeRecordStatements?.length ?? 0 }),
      value: 'change_records',
      statements: changeRecordStatements,
    },
    {
      label: t('otherDocuments', { count: otherDocsStatements?.length ?? 0 }),
      value: 'other',
      statements: otherDocsStatements,
    },
  ];

  return (
    <>
      <H2 className="mb-3 mt-5">{t('relevantContext')}</H2>
      <Tabs defaultValue="contract" className="w-full">
        <TabsList>
          {tabs.map((tab) => (
            <TabsTab key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTab>
          ))}
        </TabsList>
        {tabs.map((tab) => (
          <KeyArgumentsTab key={tab.value} value={tab.value} statements={tab.statements} />
        ))}
      </Tabs>
    </>
  );
}

function KeyArgumentsTab({
  value,
  statements,
}: {
  value: string;
  statements: ChangeAssessmentStatement[];
}) {
  const t = useTranslations('components.changeAssessments.details');
  const { projectId } = Route.useParams();
  const [loadingDocId, setLoadingDocId] = useState<string | null>(null);
  const {
    handleDocumentPreview,
    pdfPreview,
    csvPreview,
    docxPreview,
    excelPreview,
    query,
    contentPreview,
  } = useDocumentPreview({
    projectId,
  });

  const handleBadgeClick = async (docId: string, source: string, uniqueId: string) => {
    setLoadingDocId(uniqueId);
    await handleDocumentPreview({
      documentId: docId,
      source,
    });
    setLoadingDocId(null);
  };

  return (
    <TabsPanel key={value} value={value} className={'pt-4'}>
      {statements && statements.length > 0 ? (
        <ul className="list-disc list-inside space-y-2 marker:text-muted mb-1">
          {statements.map((statement, idx) => {
            const uniqueId = `${value}-${statement.id}-${idx}`;
            return (
              <li className="text-sm" key={statement.id}>
                {statement.statement}
                {statement.docId && statement.source && (
                  <Tooltip>
                    <TooltipTrigger
                      disabled={loadingDocId === uniqueId}
                      className="ml-1 disabled:opacity-75 bg-blue-3 hover:bg-blue-4 transition-colors cursor-pointer text-blue-11 rounded-md size-5 inline-flex items-center justify-center text-xs font-semibold"
                      onClick={() =>
                        handleBadgeClick(statement.docId!, statement.source!, uniqueId)
                      }
                    >
                      {idx + 1}
                    </TooltipTrigger>
                    <TooltipPopup className="max-w-52">
                      <ReactMarkdown className="prose prose-sm">{statement.source}</ReactMarkdown>
                    </TooltipPopup>
                  </Tooltip>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-muted italic">{t('noDataFound')}</p>
      )}
      <Preview
        pdf={pdfPreview.pdf}
        csv={csvPreview.csv}
        docx={docxPreview.docx}
        excel={excelPreview.excel}
        query={query}
        togglePreview={contentPreview.toggleContent}
        content={contentPreview.content}
      />
    </TabsPanel>
  );
}

function FeedbackCard({
  feedback,
  userReview,
  supplementId,
  assessmentId,
}: {
  feedback: FeedbackValue;
  userReview: string | null;
  supplementId: string;
  assessmentId: string;
}) {
  const t = useTranslations('components.feedback');
  const [textReview, setTextReview] = useState('');
  const [localFeedback, setLocalFeedback] = useState<FeedbackValue>(feedback);

  const { mutateAsync, isPending } = useSendChangeAssessmentFeedback({
    contractual_supplement_id: supplementId,
  });

  const handleSendFeedback = async () => {
    if (localFeedback === null) return;

    await mutateAsync({
      change_assessment_id: assessmentId,
      payload: {
        isPositiveFeedback: localFeedback,
        userReview: textReview,
      },
    });

    setTextReview('');
  };

  const showReviewInput = !userReview || feedback === null || localFeedback !== feedback;

  const disableBtn =
    (localFeedback === null && feedback === null) ||
    (localFeedback === feedback && !(showReviewInput && textReview.trim() !== ''));

  return (
    <Card className="space-y-4 max-w-md mt-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm -mt-1 font-medium">{t('rateVolveAI')}</h3>
          <p className="text-xs text-muted">{t('noRatingMsg')}</p>
        </div>
        <div className="flex gap-0.5">
          <FeedbackButtons
            isLoading={isPending}
            onClick={setLocalFeedback}
            feedback={localFeedback}
          />
        </div>
      </div>
      {showReviewInput && (
        <>
          <Textarea
            value={textReview}
            onChange={(e) => setTextReview(e.target.value)}
            placeholder={t('inputPlaceholder')}
            minRows={3}
          />

          <div className="flex justify-end">
            <Button onClick={handleSendFeedback} disabled={disableBtn} pending={isPending}>
              {t('buttons.submit')}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
